import AddCircle from "@mui/icons-material/AddCircle";
import CalendarMonthIcon from "@mui/icons-material/CalendarMonth";
import CommentIcon from "@mui/icons-material/Comment";
import RemoveCircleOutline from "@mui/icons-material/RemoveCircleOutline";
import { Checkbox, Chip, IconButton, TextField, Tooltip, Typography } from "@mui/material";
import { DateTime } from "luxon";
import React from "react";
import { Link } from "react-router-dom";
import { copyJsonObject } from "../commonUtils";
import { EMPLOYEE_TEMP_LABEL, FEATURES, SENIORITY_MISSING_DATA } from "../constants";
import { getThanksGivingDateOfTheYear } from "../dateHelper";
import { convertIsoDateToLuxon, convertMonthStringToInteger, set_time_to_midnight } from "../dateTimeFormatter";
import { sortSeniorityList } from "../filterSeniorityList";
import { BID_GROUP_ADD_EMPLOYEE_WARNING_MESSAGE } from "../messageConstants";
import { getTimeZoneFromSwaStation } from "../timeZoneService";
import { isAwardedBid, isDeletedBid, isPendingBid } from "./bidCommon.js";
import { getEligibleEmployees, getEmployeeUnrankedCount } from "./bidGroupUtils";
import { getFirstDayWithMonthOffset, isHoliday } from "./timeOffBidUtils";
import { parseCSVRow } from "../csvUtils";
import { IAM_DAT_BALANCES_IMPORT_ERROR_MESSAGE } from "./messageConstants";
import { IAM_DAT_BID_AWARD_TYPE } from "./bidConstants";

export const getBidGroupColumns = (
  {
    bidReferenceNumber,
    bidGroup,
    contract,
    seniorityListType,
    workgroup,
    bidStatus,
    rankStyle,
    initialBidGroup,
    userSessionEid,
    bidStation,
  },
  handleIneligibleClick,
  handleRemoveEmployee,
  handleAddRemovedEmployee,
  handleEmployeeChoicesAsAdmin,
  setSelectedEmployeeForAudit
) => {
  let columns = [];
  let baseColumns = [
    {
      field: "ineligible",
      headerName: "Ineligible",
      width: 80,
      type: "boolean",
      sortable: false,
      renderCell: (params) => {
        return (
          <Checkbox
            checked={!params.row.isEligible}
            onChange={() => {
              handleIneligibleClick(params.row.eid);
            }}
            disabled={
              !!params.row.isRemoved || params.row.rank === "-" || isDeletedBid(bidStatus) || isAwardedBid(bidStatus)
            }
          />
        );
      },
    },
    {
      field: "rank",
      headerName: "Rank",
      minWidth: 70,
      type: "string",
      sortingOrder: ["asc", "desc"],
      renderCell: (params) => {
        return (
          <TextField
            variant="standard"
            InputProps={{
              className: rankStyle,
            }}
            size="small"
            value={params.row.rank ? params.row.rank : ""}
            required
            error={params.row.rank === ""}
            disabled={!!params.row.isRemoved || isAwardedBid(bidStatus)}
          />
        );
      },
      flex: 0.12,
      sortComparator: sortSeniorityList,
      editable: isAwardedBid(bidStatus) || isDeletedBid(bidStatus) ? false : true,
    },
    {
      field: "eid",
      headerName: "EID",
      type: "string",
      sortable: false,
      minWidth: 100,
      flex: 0.15,
    },
    {
      field: "name",
      headerName: "Name",
      type: "string",
      minWidth: 220,
      sortable: false,
      renderCell: (params) => {
        return (
          <>
            {isEmployeeEligibleForIAMDATBidChoices(params.row, initialBidGroup, bidStatus, userSessionEid) ? (
              <Typography
                style={{ cursor: "pointer" }}
                fontSize="14px"
                onClick={() => {
                  handleEmployeeChoicesAsAdmin(params.row);
                }}
              >
                <u>
                  {params.row.lastName}, {params.row.shortName}
                </u>
              </Typography>
            ) : (
              <>
                {params.row.lastName}, {params.row.shortName}
              </>
            )}
            {params.row.isTemp && params.row.homeStation !== bidStation && (
              <Chip sx={{ marginLeft: "5px" }} label={EMPLOYEE_TEMP_LABEL} />
            )}
          </>
        );
      },
      flex: 0.3,
    },
    {
      field: "datBalance",
      headerName: "DAT Balance",
      type: "string",
      sortable: false,
      minWidth: 120,
      renderCell: (params) => {
        return (
          <TextField
            variant="standard"
            InputProps={{
              className: rankStyle,
            }}
            size="small"
            value={params.row.datBalance}
            required
            error={params.row.datBalance === ""}
            disabled={!!params.row.isRemoved || isAwardedBid(bidStatus) || isDeletedBid(bidStatus)}
          />
        );
      },
      flex: 0.15,
      editable: !(isAwardedBid(bidStatus) || isDeletedBid(bidStatus)),
    },
    {
      field: "phoneNumber",
      headerName: "Phone #",
      type: "string",
      minWidth: 120,
      sortable: false,
      renderCell: (params) => {
        if (!params.row.phoneNumber) return "-";
        else return params.row.phoneNumber;
      },
      flex: 0.15,
    },
  ];

  let contractBasedColumns = [];
  contractBasedColumns.push(
    ...[
      {
        field: "contractDate",
        headerName: "Contract Date",
        type: "dateTime",
        minWidth: 170,
        sortable: false,
        renderCell: (params) => {
          if (params.row.contractDate === "" || params.row.contractDate === SENIORITY_MISSING_DATA) return "-";
          return params.row.contractDate;
        },
        flex: 0.25,
      },
      {
        field: "classificationDate",
        headerName: "Classification Date",
        type: "dateTime",
        minWidth: 170,
        sortable: false,
        renderCell: (params) => {
          if (params.row.classificationDate === "" || params.row.classificationDate === SENIORITY_MISSING_DATA)
            return "-";
          return params.row.classificationDate;
        },
        flex: 0.25,
      },

      {
        field: "hireDate",
        headerName: "Hire Date",
        type: "dateTime",
        minWidth: 120,
        sortable: false,
        renderCell: (params) => {
          if (params.row.hireDate === "" || params.row.hireDate === SENIORITY_MISSING_DATA) return "-";
          return params.row.hireDate;
        },
        flex: 0.25,
      },
      {
        field: "birthDate",
        headerName: "Birth Date",
        type: "dateTime",
        minWidth: 120,
        sortable: false,
        renderCell: (params) => {
          if (params.row.birthDate === "" || params.row.birthDate === SENIORITY_MISSING_DATA) return "-";
          return params.row.birthDate;
        },
        flex: 0.25,
      },
      {
        field: "baseSchedule",
        headerName: "Base Schedule",
        type: "string",
        minWidth: 120,
        sortable: false,
        renderCell: (params) => {
          if (isEmployeeEligibleForBaseSchedule(params.row, initialBidGroup, bidStatus, bidStation)) {
            return (
              <Link
                to={{
                  pathname: `/${FEATURES.SCHEDULE_MANAGEMENT}/${bidReferenceNumber}/${params.row.eid}`,
                }}
                target="_blank"
              >
                <IconButton>
                  <CalendarMonthIcon />
                </IconButton>
              </Link>
            );
          }
        },
        flex: 0.15,
      },
    ]
  );

  const staticRightColumns = [
    {
      headerName: "",
      field: "comments",
      type: "string",
      width: 25,
      sortable: false,
      renderCell: (params) => {
        if (params?.row?.comments) {
          return <CommentIcon sx={{ cursor: "pointer" }} onClick={() => setSelectedEmployeeForAudit(params?.row)} />;
        }
      },
    },
    {
      field: "removeOrAddEmployee",
      headerName: "",
      width: 20,
      renderCell: (params) => {
        if (!params?.row?.isRemoved) {
          return (
            <IconButton
              disabled={isDeletedBid(bidStatus) || isAwardedBid(bidStatus)}
              sx={{ cursor: "pointer" }}
              onClick={() => handleRemoveEmployee(params.row)}
            >
              <RemoveCircleOutline fontSize="small" />
            </IconButton>
          );
        }

        return (
          <Tooltip
            title={
              getEmployeeUnrankedCount(getEligibleEmployees(bidGroup)) > 0 && BID_GROUP_ADD_EMPLOYEE_WARNING_MESSAGE
            }
            placement="bottom"
            arrow
          >
            <div>
              <IconButton
                disabled={
                  isDeletedBid(bidStatus) ||
                  getEmployeeUnrankedCount(getEligibleEmployees(bidGroup)) > 0 ||
                  isAwardedBid(bidStatus)
                }
                sx={{ color: "secondary.swaGray", cursor: "pointer" }}
                onClick={() => handleAddRemovedEmployee(params.row)}
              >
                <AddCircle fontSize="small" />
              </IconButton>
            </div>
          </Tooltip>
        );
      },
    },
  ];

  columns.push(...baseColumns);
  columns.push(...contractBasedColumns);
  columns.push(...staticRightColumns);

  return columns;
};

export const isEmployeeEligibleForIAMDATBidChoices = (employeeRow, initialBidGroup, bidStatus, userSessionEid) => {
  if (
    employeeRow.isRemoved ||
    employeeRow.eid === userSessionEid ||
    isPendingBid(bidStatus) ||
    isDeletedBid(bidStatus) ||
    isAwardedBid(bidStatus)
  )
    return false;
  else {
    let idx = initialBidGroup.findIndex((emp) => emp.eid === employeeRow.eid);
    if (idx === -1) return false;
  }
  return true;
};

export const handleDATBalanceCellEditCommit = (params, e, bidGroup, setBidGroup, setError) => {
  const { id, value } = params;
  const selectedEmployeeForBalance = bidGroup?.find((item) => item.eid === id);
  if (isValidDATBalance(value)) {
    const copiedBidGroup = copyJsonObject(bidGroup);
    const updatedBidGroup = copiedBidGroup.map((row) => (row.eid === id ? { ...row, datBalance: value } : { ...row }));
    setBidGroup(updatedBidGroup);
    setError(false);
  } else if (
    (value === "-" && selectedEmployeeForBalance?.datBalance === "-") ||
    (value === "" && (e.code === "Backspace" || e.code === "Delete")) ||
    value === undefined
  ) {
    setError(false);
  } else {
    setError(true);
  }
};

const isValidDATBalance = (value) => {
  return /^(2[0-5]|1\d|\d)$/.test(value) && Number(value) >= 0 && Number(value) <= 25;
};

const isEmployeeEligibleForBaseSchedule = (employeeRow, initialBidGroup, bidStatus, bidStation) => {
  if (
    employeeRow.isRemoved ||
    isDeletedBid(bidStatus) ||
    isAwardedBid(bidStatus) ||
    (employeeRow.isTemp && employeeRow.homeStation !== bidStation)
  )
    return false;
  else {
    let idx = initialBidGroup.findIndex((emp) => emp.eid === employeeRow.eid);
    if (idx === -1) return false;
  }
  return true;
};

export const getEligibleEmployeesWithInvalidBalances = (employees) => {
  return employees.filter(
    (employee) => employee.isEligible && !employee.isRemoved && !isValidDATBalance(employee.datBalance)
  );
};

export const getDefaultCalendarMonth = (station, schedMonth, schedYear) => {
  const minDate = DateTime.max(
    set_time_to_midnight(DateTime.now().setZone(getTimeZoneFromSwaStation(station))),
    getFirstDayWithMonthOffset(
      schedMonth,
      schedYear,
      process.env.REACT_APP_IAM_DAT_ALLOWED_BID_WINDOW_MONTHS_RANGE_PRIOR_TO_BID_EFFECTIVE_MONTH
    )
  );

  return minDate.toJSDate();
};

export const getMinimumAllotmentValue = (schedMonth, schedYear, date, calcDailyAllotment) => {
  const month = convertMonthStringToInteger(schedMonth);
  const year = parseInt(schedYear);
  const thanksgivingDate = getThanksGivingDateOfTheYear(year);
  const luxonDate = convertIsoDateToLuxon(date);
  if ((month === 11 || month === 12) && isHoliday(luxonDate, year, thanksgivingDate)) {
    return 0;
  }
  return calcDailyAllotment.amOrPmCount;
};

export const parseIAMDATBalancesCSV = (csvText) => {
  const rows = csvText
    .split("\n")
    .map((row) => row.trim())
    .filter((row) => row);

  if (rows.length < 2) {
    return { data: [], error: IAM_DAT_BALANCES_IMPORT_ERROR_MESSAGE };
  }

  const headers = parseCSVRow(rows[0]).map((col) => col.toLowerCase());

  if (!headers.includes("eid") || !headers.includes("dat balance")) {
    return { data: [], error: IAM_DAT_BALANCES_IMPORT_ERROR_MESSAGE };
  }

  const data = rows.slice(1).map((row) => {
    const values = parseCSVRow(row);

    return headers.reduce((acc, key, i) => {
      acc[key] = values[i]?.trim() || "";
      return acc;
    }, {});
  });

  return { data, error: null };
};

export const validateIAMDATBalancesCSV = (parsedData, bidGroup) => {
  const errors = [];

  parsedData.forEach((row, index) => {
    const rowNumber = index + 2;

    if (!/^e\d+$/.test(row.eid)) {
      errors.push(`Row ${rowNumber}: Invalid EID entered: ${row.eid}. Enter a valid EID including the "e" prefix.`);
    } else {
      const eidExistsInBidGroup = bidGroup.find((emp) => emp.eid === row.eid);
      if (!eidExistsInBidGroup) {
        errors.push(`Row ${rowNumber}: Invalid EID entered: ${row.eid}. Enter an EID from the current Bid Group.`);
      }
    }

    const datBalance = row["dat balance"];
    if (!isValidDATBalance(datBalance)) {
      errors.push(`Row ${rowNumber}: Invalid DAT balance entered: ${datBalance}. Enter a number between 0 and 25.`);
    }
  });

  return errors;
};

export const getDayOfTheWeek = (dateString) => {
  return DateTime.fromFormat(dateString, "yyyy-MM-dd").toFormat("ccc").toUpperCase();
};

export const getFormattedMonthAndDate = (dateString) => {
  return DateTime.fromFormat(dateString, "yyyy-MM-dd").toFormat("MM/dd");
};

// Below function create records for each day based on award results
export const processDayStateForIAMDATAward = (dayState, bidGroup) => {
  return dayState.flatMap((obj) => {
    const result = [];
    const date = obj.date;
    const awardedEmps = obj.awardedEmps || [];
    const amOrPmCount = obj.amOrPmCount || 0;

    // Filter awarded employees who have awardedDays and exist in bidGroup
    const eligibleEmps = awardedEmps.filter((awardedEmp) => {
      return (
        Array.isArray(awardedEmp.awardedDays) &&
        awardedEmp.awardedDays.length > 0 &&
        bidGroup.some((bidGroupEmp) => bidGroupEmp.eid === awardedEmp.eid)
      );
    });

    // Create records based on amOrPmCount
    for (let i = 0; i < amOrPmCount; i++) {
      const eligibleEmp = eligibleEmps[i];
      if (eligibleEmp) {
        const bidGroupEmp = bidGroup.find((emp) => emp.eid === eligibleEmp.eid);
        result.push({
          ...eligibleEmp,
          date: date,
          isManuallyAddedToBidGroup: bidGroupEmp["isManuallyAddedToBidGroup"],
        });
      } else {
        result.push({ date: date });
      }
    }

    return result;
  });
};

export const generateIAMDATDayStateRows = (allotmentDayState, bidGroup) => {
  const iamDatResults = processDayStateForIAMDATAward(allotmentDayState, bidGroup);
  return iamDatResults.sort((a, b) => a.date.localeCompare(b.date)).map((obj, index) => ({ id: index + 1, ...obj }));
};

export const getUnAwardedTableColumns = ({ station, bidReferenceNumber }) => {
  let columns = [
    {
      field: "awardType",
      headerName: "Bid",
      sortable: false,
      flex: 0.15,
    },
    {
      field: "rank",
      headerName: "Rank",
      sortable: false,
      flex: 0.15,
    },
    {
      field: "eid",
      headerName: "EID",
      sortable: false,
      flex: 0.2,
    },
    {
      field: "name",
      headerName: "Name",
      sortable: false,
      flex: 0.5,
      renderCell: (params) => {
        return (
          <>
            {params.row.lastName && params.row.shortName ? params.row.lastName + ", " + params.row.shortName : ""}
            {params.row.isTemp && params.row.homeStation !== station && (
              <Chip sx={{ marginLeft: "5px" }} label={EMPLOYEE_TEMP_LABEL} />
            )}
          </>
        );
      },
    },
    {
      field: "baseSchedule",
      headerName: "Base Schedule",
      type: "string",
      minWidth: 120,
      sortable: false,
      renderCell: (params) => {
        if (params.row.awardType === "NS") {
          return (
            <Link
              to={{
                pathname: `/${FEATURES.SCHEDULE_MANAGEMENT}/${bidReferenceNumber}/${params.row.eid}`,
              }}
              target="_blank"
            >
              <IconButton>
                <CalendarMonthIcon />
              </IconButton>
            </Link>
          );
        }
      },
      flex: 0.15,
    },
  ];

  return columns;
};

export const getIAMDATColumns = ({ station, handleAwardedNameClick }) => {
  let columns = [
    {
      field: "date",
      headerName: "Day",
      sortable: false,
      flex: 0.15,
      renderCell: (params) => {
        return params.row.date ? DateTime.fromFormat(params.row.date, "yyyy-MM-dd").toFormat("MM/dd") : params.row.id;
      },
    },
    {
      field: "awardType",
      headerName: "Award",
      sortable: false,
      flex: 0.15,
      renderCell: (params) => {
        return params.row.awardType;
      },
    },
    {
      field: "awardedRank",
      headerName: "Rank",
      sortable: false,
      flex: 0.15,
      renderCell: (params) => {
        return params.row.awardedRank;
      },
    },
    {
      field: "awardedEid",
      headerName: "EID",
      sortable: false,
      flex: 0.15,
      renderCell: (params) => {
        return params.row.eid;
      },
    },
    {
      field: "awardedName",
      headerName: "Name",
      sortable: false,
      flex: 0.4,
      renderCell: (params) => {
        return (
          <>
            <Typography
              style={{ cursor: "pointer" }}
              fontSize="14px"
              onClick={() => {
                handleAwardedNameClick(params.row);
              }}
            >
              <u>
                {params.row.lastName && params.row.firstName ? params.row.lastName + ", " + params.row.firstName : ""}
              </u>
            </Typography>
            {params.row.isTemp && params.row.homeStation !== station && (
              <Chip sx={{ marginLeft: "5px" }} label={EMPLOYEE_TEMP_LABEL} />
            )}
          </>
        );
      },
    },
  ];
  return columns;
};

export const hasEmployeesMarkedNS = (employees) => {
  return employees.filter((emp) => emp?.awardType === IAM_DAT_BID_AWARD_TYPE.NO_SCHEDULE).length > 0;
};

export const getEligibleEmployeesWithInvalidBalanceCount = (employees) => {
  return employees.filter(
    (employee) => employee.isEligible && !employee.isRemoved && !isValidDATBalance(employee.datBalance)
  ).length;
};
